/**
 * Route copy for `src/routes/prospect` (keys: `routes.prospect.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  addToListModal: {
    alreadyPresentSuffix: ', {{count}} already present',
    couldNotLoadTheLists: 'Could not load the lists.',
    nameTheList: 'Name the list.',
    listNameCreatedWithMemberCount: 'List “{{name}}” created with {{member_count}} contact(s).',
    chooseAList: 'Choose a list.',
    addedAddedV: '{{added}} added{{v}}.',
    addingToTheListFailedTry: 'Adding to the list failed. Try again.',
    addLengthContactSToA: 'Add {{length}} contact(s) to a list',
    cancel: 'Cancel',
    adding: 'Adding…',
    add: 'Add',
    existingList: 'Existing list',
    newList: 'New list',
    list: 'List',
    listName: 'List name',
    eGSaasDecisionMakersQuebec: 'E.g. SaaS decision-makers Quebec',
  },
  addToSegmentModal: {
    alreadyPresentSuffix: ', {{count}} already present',
    couldNotLoadTheSegments: 'Could not load the segments.',
    nameTheSegment: 'Name the segment.',
    segmentNameCreatedWithMemberCount: 'Segment “{{name}}” created with {{member_count}} member(s).',
    chooseASegment: 'Choose a segment.',
    addedAddedV: '{{added}} added{{v}}.',
    addingToTheSegmentFailedTry: 'Adding to the segment failed. Try again.',
    addLengthToTheSegment: 'Add {{length}} to the segment',
    cancel: 'Cancel',
    adding: 'Adding…',
    add: 'Add',
    existingSegment: 'Existing segment',
    newSegment: 'New segment',
    segmentStatic: 'Segment (static)',
    segmentName: 'Segment name',
    eGQ3TargetAccounts: 'E.g. Q3 target accounts',
  },
  enrichmentPanel: {
    inProgress: 'In progress…',
    verified: 'Verified',
    revealing: 'Revealing…',
    theProviderApolloDeliversThisNumber: 'The provider (Apollo) delivers this number asynchronously. Rerun the enrichment in a moment to retrieve the value — no fabricated number is shown.',
    viaSource: 'via {{source}}',
    traceabilityCascade: 'Traceability (cascade)',
    provider: 'Provider',
    field: 'Field',
    value: 'Value',
    status: 'Status',
    verif: 'Verif.',
    verifiedEnrichmentAppliedToTheContact: 'Verified enrichment applied to the contact.',
    theRemainingProvidersNeedAnApi: 'The remaining providers need an API key to go further.',
    enrichmentFailedTryAgain: 'Enrichment failed. Try again.',
    enrichmentName: 'Enrichment — {{name}}',
    close: 'Close',
    cascadeInProgress: 'Cascade in progress…',
    retry: 'Retry',
    startTheEnrichment: 'Start the enrichment',
    cascadeSearchInTheInternalBase: 'Cascade search in the internal base, then generation of email patterns (firstname.lastname@…) verified in real time (MX + SMTP probe) and E.164 validation of the phone. Only a genuinely verified value is kept.',
    verifyTheEmailMxSmtp: 'Verify the email (MX + SMTP)',
    verifyThePhoneE164: 'Verify the phone (E.164)',
    addAnApiKeyHunterClearbit: 'Add an API key (Hunter / Clearbit / Apollo) on the service side to enable these providers.',
    email: 'Email',
    phone: 'Phone',
    verifiedResultSavedOnTheContact: 'Verified result saved on the contact record.',
  },
  importWizard: {
    mergeDuplicatesFillEmptyFields: 'Merge duplicates (fill empty fields)',
    skipDuplicates: 'Skip duplicates',
    alwaysCreateNoDeduplication: 'Always create (no deduplication)',
    chooseACsvOrXlsxFile: 'Choose a .csv or .xlsx file.',
    theFileCouldNotBeParsed: 'The file could not be parsed. Check the format.',
    mapAtLeastOneColumnTo: 'Map at least one column to a field.',
    thePreviewFailedCheckTheColumn: 'The preview failed. Check the column mapping.',
    importedImportedMergedMerged: '{{imported}} imported, {{merged}} merged',
    enrichedEnriched: ', {{enriched}} enriched',
    skippedDuplicateDuplicateS: ', {{skipped_duplicate}} duplicate(s)',
    insufficientCreditsForEnrichmentTryAgain: 'Insufficient credits for enrichment. Try again without enriching.',
    theImportFailed: 'The import failed.',
    cancel: 'Cancel',
    analyzing: 'Analyzing…',
    analyzeTheFile: 'Analyze the file',
    back: 'Back',
    preview: 'Preview',
    importing: 'Importing…',
    importV: 'Import {{v}}',
    importDataCsvExcel: 'Import data (CSV / Excel)',
    file: 'File',
    columns: 'Columns',
    recordType: 'Record type',
    contacts: 'Contacts',
    companies: 'Companies',
    acceptedFormatsCsvXlsxTsvJson: 'Accepted formats: .csv, .xlsx, .tsv, .json (header required).',
    rowSDetectedMapEachColumn: 'row(s) detected. Map each column to a field.',
    fileColumn: 'File column',
    example: 'Example',
    targetField: 'Target field',
    targetFieldForCol: 'Target field for {{col}}',
    ignore: '— Ignore —',
    duplicates: 'Duplicates',
    enrichTheNewContactsBilled: 'Enrich the new contacts (billed)',
    total: 'Total',
    new: 'New',
    duplicatesBase: 'Duplicates (base)',
    duplicatesFile: 'Duplicates (file)',
    invalid: 'Invalid',
    status: 'Status',
    previewOnlyNothingIsWrittenUntil: 'Preview only — nothing is written until you click “Import”.',
  },
  prospectLayout: {
    b2bSearch: 'B2B search',
    myLists: 'My lists',
    b2bProspecting: 'B2B prospecting',
    companyAndContactBaseListBuilding: 'Company and contact base, list building and cascade enrichment.',
    prospectingNavigation: 'Prospecting navigation',
  },
  prospectListsPage: {
    justNow: 'just now',
    vMinAgo: '{{v}} min ago',
    vHAgo: '{{v}} h ago',
    vDAgo: '{{v}} d ago',
    couldNotLoadTheMembers: 'Could not load the members.',
    exportedExportedToTheCrm: '{{exported}} exported to the CRM',
    alreadyExportedAlreadyPresent: ', {{already_exported}} already present',
    failedFailureS: ', {{failed}} failure(s)',
    exportToTheCrmFailed: 'Export to the CRM failed.',
    contact: 'Contact',
    exported: 'Exported',
    remove: 'Remove',
    memberCountMemberS: '{{member_count}} member(s)',
    readOnly: 'Read-only',
    addToTheCrmLength: 'Add to the CRM ({{length}})',
    addAllToTheCrm: 'Add all to the CRM',
    loadingMembers: 'Loading members…',
    thisListIsEmptyAddContacts: 'This list is empty. Add contacts from the B2B search.',
    noMembers: 'No members.',
    listName: 'List name',
    couldNotLoadTheLists: 'Could not load the lists.',
    list: 'List',
    members: 'Members',
    created: 'Created',
    delete: 'Delete',
    myLists: 'My lists',
    segments: 'Segments',
    savedProspectingListsOpenAList: 'Saved prospecting lists. Open a list to export it to the CRM.',
    newList: '+ New list',
    loadingLists: 'Loading lists…',
    noListsSelectContactsInThe: 'No lists. Select contacts in the B2B search to create one.',
    searchForAList: 'Search for a list…',
    noList: 'No list.',
    newProspectingList: 'New prospecting list',
    eGSaasDecisionMakersQuebec: 'E.g. SaaS decision-makers Quebec',
    create: 'Create',
    listNameCreated: 'List “{{name}}” created.',
    deleteTheList: 'Delete the list',
    deleteNameAndItsMemberCount: 'Delete “{{name}}” and its {{member_count}} member(s)?',
    listDeleted: 'List deleted.',
  },
  prospectSearchPage: {
    industry: 'Industry',
    headcount: 'Headcount',
    seniority: 'Seniority',
    department: 'Department',
    theSearchFailedTryAgain: 'The search failed. Try again.',
    contact: 'Contact',
    company: 'Company',
    email: 'Email',
    country: 'Country',
    enrich: 'Enrich',
    viewContacts: 'View contacts',
    selectV: 'Select {{v}}',
    contacts: 'Contacts',
    companies: 'Companies',
    nameTitleCompanyEmail: 'Name, title, company, email…',
    companyNameDomainIndustry: 'Company name, domain, industry…',
    search: 'Search',
    vAll: '{{v}} (all)',
    knownEmail: 'Known email',
    verified: 'Verified',
    advancedFilters: 'Advanced filters',
    reset: 'Reset',
    import: 'Import',
    entityType: 'Entity type',
    postalCode: 'Postal code',
    city: 'City',
    region: 'Region',
    latitude: 'Latitude',
    longitude: 'Longitude',
    radiusKm: 'Radius (km)',
    requiresAPointLatitudeLongitudeOr: 'Requires a point (latitude + longitude) or a postal code above.',
    technology: 'Technology',
    reactSalesforce: 'React, Salesforce…',
    jobTitle: 'Job title',
    directorVp: 'Director, VP…',
    clearTheAdvancedFilters: 'Clear the advanced filters',
    selected: 'selected',
    addToAList: 'Add to a list',
    addToASegment: 'Add to a segment',
    clearTheSelection: 'Clear the selection',
    searching: 'Searching…',
    noContactInTheBaseMatches: 'No contact in the base matches. Ingest data or broaden the filters.',
    noContacts: 'No contacts.',
    noCompanyMatchesTheseCriteria: 'No company matches these criteria.',
    noCompanies: 'No companies.',
    rangetoOfTotal: '–{{rangeTo}} of {{total}}',
    previous: 'Previous',
    next: 'Next',
  },
  savedSearchBar: {
    cadenceSaved: 'Cadence saved.',
    cadenceDisabled: 'Cadence disabled.',
    couldNotSaveTheCadenceTry: 'Could not save the cadence. Try again.',
    addedAddedMatchedMatchEs: '{{added}} added · {{matched}} match(es)',
    enrichedEnriched: ' · {{enriched}} enriched',
    theCadenceIsDisabledEnableIt: 'The cadence is disabled — enable it first.',
    noTargetListEnterAList: 'No target list — enter a list or leave the field empty to create one.',
    couldNotRun: 'Could not run.',
    couldNotRunImmediatelyTryAgain: 'Could not run immediately. Try again.',
    cadenceName: 'Cadence — “{{name}}”',
    close: 'Close',
    saving: 'Saving…',
    saveTheCadence: 'Save the cadence',
    automaticReRunEnabled: 'Automatic re-run enabled',
    frequency: 'Frequency',
    targetListOptional: 'Target list (optional)',
    listIdEmptyListCreatedAutomatically: 'List ID — empty = list created automatically',
    automaticallyEnrichNewResults: 'Automatically enrich new results',
    nextRun: 'Next run:',
    lastRun: 'Last run:',
    scheduleLastAddedAdded: ' · {{schedule_last_added}} added',
    running: 'Running…',
    runNow: 'Run now',
    foundAddedAdded: 'found · {{added}} added',
    alreadyInListAlreadyPresent: ' · {{already_in_list}} already present',
    runNowRunsACycleImmediately: '“Run now” runs a cycle immediately (adds the new results to the target list), even if the background scheduler is paused.',
    searchNameLoaded: 'Search “{{name}}” loaded.',
    searchDeleted: 'Search deleted.',
    couldNotDelete: 'Could not delete.',
    savedSearches: 'Saved searches',
    savedSearches2: 'Saved searches…',
    cadence: 'Cadence ⏱',
    schedule: 'Schedule',
    delete: 'Delete',
    saveTheSearch: 'Save the search',
    searchName: 'Search name',
    eGSalesVpsQuebec: 'E.g. Sales VPs — Quebec',
    save: 'Save',
    searchNameSaved: 'Search “{{name}}” saved.',
  },
  segmentsManager: {
    dynamic: 'Dynamic',
    static: 'Static',
    nameTheSegment: 'Name the segment.',
    segmentNameCreated: 'Segment “{{name}}” created.',
    creatingTheSegmentFailed: 'Creating the segment failed.',
    newSegment: 'New segment',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    name: 'Name',
    eGSaasDecisionMakersQuebec: 'E.g. SaaS decision-makers Quebec',
    entity: 'Entity',
    contacts: 'Contacts',
    companies: 'Companies',
    type: 'Type',
    membersComputedByFilter: 'Members computed by filter.',
    membersAddedByHand: 'Members added by hand.',
    dynamicFilter: 'Dynamic (filter)',
    staticManual: 'Static (manual)',
    membershipCriteriaEveryMatchingRecordJoi: 'Membership criteria — every matching record joins the segment automatically.',
    searchText: 'Search (text)',
    nameTitleCompany: 'Name, title, company…',
    nameDomainIndustry: 'Name, domain, industry…',
    seniority: 'Seniority',
    all: 'All',
    industry: 'Industry',
    softwareLegal: 'Software, Legal…',
    country: 'Country',
    canada: 'Canada',
    couldNotLoadTheMembers: 'Could not load the members.',
    exportedExportedToTheCrm: '{{exported}} exported to the CRM',
    alreadyExportedAlreadyPresent: ', {{already_exported}} already present',
    failedFailureS: ', {{failed}} failure(s)',
    exportToTheCrmFailed: 'Export to the CRM failed.',
    totalMemberS: '· {{total}} member(s)',
    exportToTheCrm: 'Export to the CRM',
    dynamicSegmentMembersAreRecomputedLive: 'Dynamic segment: members are recomputed live from the criteria. A new matching record appears automatically.',
    loadingMembers: 'Loading members…',
    noRecordMatchesTheCriteriaYet: 'No record matches the criteria yet.',
    emptySegmentAddMembersFromThe: 'Empty segment. Add members from the B2B search.',
    exported: 'Exported',
    remove: 'Remove',
    couldNotLoadTheSegments: 'Could not load the segments.',
    contactOrCompanyListsStaticExplicit: 'Contact or company lists — static (explicit members) or dynamic (filter-based membership, updated automatically).',
    newSegment2: '+ New segment',
    loadingSegments: 'Loading segments…',
    noSegmentsCreateADynamicOne: 'No segments. Create a dynamic one by filter, or a static one from the B2B search.',
    memberCountMemberS: '· {{member_count}} member(s)',
    delete: 'Delete',
    deleteTheSegment: 'Delete the segment',
    deleteName: 'Delete “{{name}}”?',
    segmentDeleted: 'Segment deleted.',
  },
};

export default en;
